import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import Toast from '../components/Toast';
import api from '../services/api';
import { formatLocalDateOnly, formatLocalDateTime } from '../utils/timezone';

type ToastType = 'success' | 'error' | 'info' | 'warning';

type APITokenScope = 'legacy_api' | 'appointments_api';

const API_TOKEN_SCOPES: {
  value: APITokenScope;
  label: string;
  description: string;
}[] = [
  {
    value: 'legacy_api',
    label: 'Legacy API',
    description: '/api/v1/available-hours/, /api/v1/book/, /api/v1/info/',
  },
  {
    value: 'appointments_api',
    label: 'Partner Appointments API',
    description: '/api/v1/appointments/available-slots/, /api/v1/appointments/book/',
  },
];

const formatScopes = (scopes?: APITokenScope[]) => {
  if (!scopes || scopes.length === 0) {
    return 'No scopes';
  }

  return scopes
    .map((scope) => API_TOKEN_SCOPES.find((item) => item.value === scope)?.label || scope)
    .join(', ');
};

interface APIToken {
  id: number;
  name: string;
  token: string;
  description?: string;
  allowed_host: string;
  allowed_ips?: string[];
  scopes?: APITokenScope[];
  is_active: boolean;
  created_by: number;
  created_by_name: string;
  last_used_at?: string;
  expires_at?: string;
  is_valid: boolean;
  created_at: string;
  updated_at: string;
}

const APITokensPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [tokens, setTokens] = useState<APIToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      const response = await api.get('/api/manage/tokens/');
      const tokensData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || []);
      setTokens(tokensData);
    } catch (err: any) {
      console.error('Failed to load tokens:', err);
      setToast({ message: '✗ Failed to load API tokens', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (token: string) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(token);
    setToast({ message: '✓ Token copied to clipboard!', type: 'success' });
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const toggleTokenActive = async (tokenId: number) => {
    try {
      await api.post(`/api/manage/tokens/${tokenId}/toggle_active/`);
      setToast({ message: '✓ Token status updated', type: 'success' });
      loadTokens();
    } catch (err: any) {
      setToast({ message: '✗ Failed to update token', type: 'error' });
    }
  };

  const deleteToken = async (tokenId: number, tokenName: string) => {
    if (!window.confirm(`Delete API token "${tokenName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/api/manage/tokens/${tokenId}/`);
      setToast({ message: '✓ Token deleted', type: 'success' });
      loadTokens();
    } catch (err: any) {
      setToast({ message: '✗ Failed to delete token', type: 'error' });
    }
  };

  // Check if user is Owner
  if (currentUser && !currentUser.can_manage_users) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
              <h1 className="text-2xl font-bold text-red-900 mb-4">⛔ Access Denied</h1>
              <p className="text-red-800">Only Owners can manage API tokens.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
            <p className="mt-4 text-gray-600">Loading API tokens...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">🔑 API Tokens</h1>
              <p className="mt-2 text-gray-600">
                Manage API tokens for external integrations
              </p>
            </div>

            {/* Create Token Button - Only for Owner */}
            {currentUser?.can_manage_users && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-accent-500 text-white rounded-xl hover:bg-accent-600 transition font-semibold shadow-lg"
              >
                <svg className="h-5 w-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create New Token
              </button>
            )}
          </div>

          {/* Info Banner */}
          <div className="bg-primary-50 border-2 border-primary-200 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-primary-900 mb-2">ℹ️ About API Tokens</h3>
            <p className="text-sm text-primary-700 mb-2">
              API tokens allow external applications (like your company website) to access the Saas System API.
              Keep tokens secure and never commit them to version control.
            </p>
            <p className="text-sm text-primary-700 mb-2">
              <strong>Important:</strong> Each token must specify an allowed host. Use <code className="bg-primary-100 px-1 rounded">*</code> to allow any host.
              CORS is disabled for API token requests — host validation is done in authentication.
            </p>
            <p className="text-sm text-primary-700">
              <strong>Scopes:</strong> Choose which API groups the token can access. Legacy API covers the original endpoints;
              Partner Appointments API covers the new partner integration endpoints.
            </p>
          </div>

          {/* Tokens List */}
          <div className="space-y-4">
            {tokens.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-6 py-12 text-center">
                <p className="text-gray-500">No API tokens created yet</p>
                <p className="text-sm text-gray-400 mt-2">Click "Create New Token" to get started</p>
              </div>
            ) : (
              tokens.map((token) => (
                <div
                  key={token.id}
                  className={`bg-white rounded-xl shadow-sm border-2 transition overflow-hidden ${
                    token.is_valid
                      ? 'border-green-200 hover:border-green-300'
                      : 'border-gray-200 opacity-60'
                  }`}
                >
                  {/* Token Header */}
                  <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-xl font-bold text-gray-900">{token.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold border-2 ${
                            token.is_valid
                              ? 'bg-green-100 text-green-800 border-green-300'
                              : 'bg-red-100 text-red-800 border-red-300'
                          }`}>
                            {token.is_valid ? '✓ Active' : '⛔ Inactive'}
                          </span>
                        </div>
                        {token.description && (
                          <p className="text-sm text-gray-600 mt-1">{token.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          <span className="font-semibold">Allowed Host:</span> {token.allowed_host} •{' '}
                          <span className="font-semibold">Scopes:</span> {formatScopes(token.scopes)} •
                          Created by {token.created_by_name} • {formatLocalDateOnly(token.created_at)}
                          {token.last_used_at && ` • Last used: ${formatLocalDateTime(token.last_used_at)}`}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {token.scopes && token.scopes.map((scope) => {
                            const scopeMeta = API_TOKEN_SCOPES.find((item) => item.value === scope);
                            return (
                              <span
                                key={scope}
                                className="px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200"
                              >
                                {scopeMeta?.label || scope}
                              </span>
                            );
                          })}
                          {token.allowed_ips && token.allowed_ips.length > 0 && (
                            <span className="px-2 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
                              IP restricted: {token.allowed_ips.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2">
                        {/* Toggle Active */}
                        <button
                          onClick={() => toggleTokenActive(token.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                            token.is_active
                              ? 'bg-orange-500 text-white hover:bg-orange-600'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          {token.is_active ? 'Disable' : 'Enable'}
                        </button>

                        {/* Delete - Only Owner */}
                        {currentUser?.can_manage_users && (
                          <button
                            onClick={() => deleteToken(token.id, token.name)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Token Value */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="flex-1">
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">API Token:</label>
                        <div className="mt-1 flex items-center space-x-2">
                          <code className="block bg-white px-4 py-2 rounded-lg border border-gray-300 text-sm font-mono text-gray-800 flex-1">
                            {token.token}
                          </code>
                          <button
                            onClick={() => copyToClipboard(token.token)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                              copiedToken === token.token
                                ? 'bg-green-500 text-white'
                                : 'bg-accent-500 text-white hover:bg-accent-600'
                            }`}
                          >
                            {copiedToken === token.token ? (
                              <>
                                <svg className="h-4 w-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Copied!
                              </>
                            ) : (
                              <>
                                <svg className="h-4 w-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Usage Example */}
                    <div className="mt-4">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Usage Example:</label>
                      <pre className="mt-1 bg-gray-900 text-green-400 px-4 py-3 rounded-lg text-xs font-mono overflow-x-auto">
{`curl -X GET ${process.env.REACT_APP_API_URL || 'https://your-domain.com'}/api/v1/health-check/ \\
  -H "X-API-Token: ${token.token}"`}
                      </pre>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Endpoints Documentation */}
          <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">📚 Available API Endpoints</h3>
            <div className="space-y-3">
              <div className="border-l-4 border-green-500 pl-4">
                <p className="font-semibold text-gray-900">GET /api/v1/health-check/</p>
                <p className="text-sm text-gray-600">Check system health status</p>
                <p className="text-xs text-gray-500 mt-1">Returns: 200 OK if healthy, 503 if issues detected</p>
              </div>
              
              <div className="border-l-4 border-accent-500 pl-4">
                <p className="font-semibold text-gray-900">GET /api/v1/info/</p>
                <p className="text-sm text-gray-600">Get API information and available endpoints</p>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <p className="font-semibold text-gray-900">GET /api/v1/available-hours/</p>
                <p className="text-sm text-gray-600">Get available booking slots for all managers (next 4 days)</p>
                <p className="text-xs text-gray-500 mt-1">Returns: List of dates with available 30-min time slots • Scope: legacy_api</p>
              </div>

              <div className="border-l-4 border-orange-500 pl-4">
                <p className="font-semibold text-gray-900">POST /api/v1/book/</p>
                <p className="text-sm text-gray-600">Create a new booking for a client</p>
                <p className="text-xs text-gray-500 mt-1">Required: client_name, client_email, start_time • Scope: legacy_api</p>
              </div>

              <div className="border-l-4 border-indigo-500 pl-4">
                <p className="font-semibold text-gray-900">POST /api/v1/appointments/available-slots/</p>
                <p className="text-sm text-gray-600">Get available appointment slots for partner integrations</p>
                <p className="text-xs text-gray-500 mt-1">Required: requestedDates[] • Scope: appointments_api</p>
              </div>

              <div className="border-l-4 border-pink-500 pl-4">
                <p className="font-semibold text-gray-900">POST /api/v1/appointments/book/</p>
                <p className="text-sm text-gray-600">Book an appointment using a signed slotId</p>
                <p className="text-xs text-gray-500 mt-1">Required: slotId, lead • Scope: appointments_api</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Token Modal */}
      {showCreateModal && <CreateTokenModal onClose={() => setShowCreateModal(false)} onCreated={loadTokens} setToast={setToast} />}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

// Create Token Modal Component
interface CreateTokenModalProps {
  onClose: () => void;
  onCreated: () => void;
  setToast: (toast: { message: string; type: ToastType }) => void;
}

const CreateTokenModal: React.FC<CreateTokenModalProps> = ({ onClose, onCreated, setToast }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [allowedHost, setAllowedHost] = useState('');
  const [allowedIpsRaw, setAllowedIpsRaw] = useState('');
  const [scopes, setScopes] = useState<APITokenScope[]>(['legacy_api', 'appointments_api']);
  const [loading, setLoading] = useState(false);

  const toggleScope = (scope: APITokenScope) => {
    setScopes((current) =>
      current.includes(scope)
        ? current.filter((item) => item !== scope)
        : [...current, scope]
    );
  };

  const parseIps = (raw: string): string[] =>
    raw
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setToast({ message: '⚠ Please enter a token name', type: 'warning' });
      return;
    }

    if (!allowedHost.trim()) {
      setToast({ message: '⚠ Please enter an allowed host', type: 'warning' });
      return;
    }

    if (scopes.length === 0) {
      setToast({ message: '⚠ Select at least one API scope', type: 'warning' });
      return;
    }

    const allowedIps = parseIps(allowedIpsRaw);

    setLoading(true);
    try {
      await api.post('/api/manage/tokens/', {
        name: name.trim(),
        description: description.trim() || undefined,
        allowed_host: allowedHost.trim(),
        allowed_ips: allowedIps,
        scopes,
      });
      
      setToast({ message: '✓ API token created successfully!', type: 'success' });
      onCreated();
      onClose();
    } catch (err: any) {
      console.error('Create token error:', err);
      const errorMessage = err.response?.data?.scopes?.[0] ||
                          err.response?.data?.allowed_ips?.[0] ||
                          err.response?.data?.allowed_host?.[0] || 
                          err.response?.data?.detail || 
                          err.response?.data?.error ||
                          err.message;
      setToast({ 
        message: `✗ Failed to create token: ${errorMessage}`, 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Create New API Token</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Token Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Website API, Mobile App"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allowed Host <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={allowedHost}
                onChange={(e) => setAllowedHost(e.target.value)}
                placeholder="e.g., example.com or subdomain.example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Domain from which requests with this token will be allowed. Use <code>*</code> for any host.
                Do not include http:// or https://
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IP Allowlist (Optional)
              </label>
              <textarea
                value={allowedIpsRaw}
                onChange={(e) => setAllowedIpsRaw(e.target.value)}
                placeholder={'1.2.3.4\n5.6.7.0/24\n2001:db8::1'}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                One IP or CIDR per line (or comma-separated). Leave empty to allow any IP.
                <br />
                <strong className="text-amber-700">If set, IP is checked first — wrong IP = 403 even if host is correct.</strong>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Scopes <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {API_TOKEN_SCOPES.map((scope) => (
                  <label
                    key={scope.value}
                    className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={scopes.includes(scope.value)}
                      onChange={() => toggleScope(scope.value)}
                      className="mt-1 h-4 w-4 text-accent-600 border-gray-300 rounded focus:ring-accent-500"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-900">{scope.label}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">{scope.description}</span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Partner integrations usually need only <strong>Partner Appointments API</strong>.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What will this token be used for?"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800">
                ⚠️ <strong>Important:</strong> The token will be shown only once. Make sure to copy and save it securely.
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 rounded-lg transition font-semibold ${
                loading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              {loading ? 'Creating...' : 'Create Token'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default APITokensPage;

